import { Suspense } from 'react';
import { Navigate, Outlet, useLocation, useMatches } from 'react-router';

import { posthogIdentify, posthogPageview } from '@/lib/posthog';
import { getLoginRedirectTarget } from '@/modules/auth/application/login-route';
import { useFirebase } from '@/modules/common/application/context/firebase';
import { useUser } from '@/modules/common/application/context/user';
import { roleFallback } from '@/modules/common/application/route-fallback';
import { Loader } from '@/modules/common/components';
import { CompanyStatus, ContextUser, UserAccountStatus, UserRole } from '@/modules/common/types';
import { ContextCompany } from '@/modules/common/types/company';

type HandleType = {
  allowedRoles?: UserRole[];
  surveyShell?: boolean;
};

type MatchWithHandle = {
  handle?: HandleType;
};

type Step =
  | '/set-password'
  | '/onboarding-start'
  | '/user-info'
  | '/company-info'
  | '/add-facility';

const STEPS: Step[] = [
  '/set-password',
  '/onboarding-start',
  '/user-info',
  '/company-info',
  '/add-facility',
];

function progressIndex(user?: ContextUser, company?: ContextCompany): number {
  let idx = -1;

  if (user?.status === UserAccountStatus.ACCOUNT_CREATED) return 0;

  if (user?.status === UserAccountStatus.NEW_PASSWORD_SET) {
    idx = Math.max(idx, 1);
    idx = Math.max(idx, 2);
  }

  if (company?.status === CompanyStatus.COMPANY_CREATED) {
    idx = Math.max(idx, 3);
  }

  if (company?.status === CompanyStatus.COMPANY_INFO_COMPLETED) {
    idx = Math.max(idx, 4);
  }

  return idx;
}

function nextOnboardingRoute(
  user?: ContextUser,
  company?: ContextCompany,
  path = ''
): string | null {
  const maxAllowed = progressIndex(user, company);
  if (maxAllowed < 0) return null;

  if (STEPS.slice(0, maxAllowed + 1).includes(path as Step)) {
    return null;
  }

  return STEPS[maxAllowed];
}

export function ProtecedRoute() {
  const loc = useLocation();
  const { authenticated, loading: isFirebaseLoading } = useFirebase();
  const { user, company, isDeactivated, loadingUserData } = useUser();
  const matches = useMatches() as (MatchWithHandle & Record<string, any>)[];
  const lastMatch = matches[matches.length - 1];
  const allowedRoles = lastMatch.handle?.allowedRoles as UserRole[];
  const surveyShell = lastMatch.handle?.surveyShell as boolean;

  if (isFirebaseLoading || loadingUserData || (!user && authenticated)) {
    return <Loader />;
  }

  if (!authenticated || !user) {
    const redirectPath = loc.pathname + loc.search;

    return (
      <Navigate
        to={getLoginRedirectTarget(redirectPath)}
        state={{ redirect: redirectPath }}
        replace
      />
    );
  }

  if (isDeactivated && loc.pathname !== '/company-deactivated') {
    return <Navigate to="/company-deactivated" replace />;
  }

  const next = nextOnboardingRoute(user, company, loc.pathname);
  if (next && next !== loc.pathname) return <Navigate to={next} replace />;
  if (allowedRoles && !allowedRoles.includes(user.role))
    return <Navigate to={roleFallback(user.role, company?.useNewVersion)} replace />;

  posthogIdentify(user, company);
  posthogPageview(window.location.href, surveyShell ? 'survey' : 'app');

  return (
    <Suspense fallback={<Loader />}>
      <Outlet />
    </Suspense>
  );
}
